import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function TestersAdmin({ token }) {
  const [testers, setTesters] = useState([]);
  const [form, setForm] = useState({ username: '', password: '' });
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const cfg = { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/testers`, cfg);
      setTesters(data.testers || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load testers');
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg) => {
    setError('');
    setNotice('');
    try { await fn(); setNotice(okMsg); load(); return true; } catch (err) { setError(err?.response?.data?.error || 'Action failed'); return false; }
  };

  const create = async (e) => {
    e.preventDefault();
    const ok = await run(() => axios.post(`${API_URL}/api/admin/testers`, form, cfg), `Tester "${form.username}" created`);
    if (ok) setForm({ username: '', password: '' });
  };
  const savePassword = async (t) => {
    const ok = await run(() => axios.put(`${API_URL}/api/admin/testers/${t.tester_id}`, { password: resetPw }, cfg), `Password reset for ${t.username}`);
    if (ok) { setResetFor(null); setResetPw(''); }
  };
  const toggle = (t) => run(() => axios.put(`${API_URL}/api/admin/testers/${t.tester_id}`, { is_active: !t.is_active }, cfg),
    `${t.username} ${t.is_active ? 'disabled' : 'enabled'}`);

  return (
    <div data-testid="testers-admin">
      <form onSubmit={create} className="card p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="tester-create-form">
        <div>
          <label className="input-label block mb-1">Username</label>
          <input className="input-styled" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="tester-create-username" />
        </div>
        <div>
          <label className="input-label block mb-1">Password (min 8)</label>
          <input className="input-styled" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="tester-create-password" />
        </div>
        <button type="submit" className="btn-primary px-4 py-2" data-testid="tester-create-btn">Create tester</button>
      </form>

      {error && <div className="p-3 mb-4 rounded-lg text-sm" style={{ background: '#FEE2E2', color: '#B91C1C' }} data-testid="testers-error">{error}</div>}
      {notice && <div className="p-3 mb-4 rounded-lg text-sm" style={{ background: '#DCFCE7', color: '#166534' }} data-testid="testers-notice">{notice}</div>}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="testers-table">
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              {['Username', 'Status', 'Created', 'Last sign-in', 'Actions'].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--color-muted)' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {testers.map((t) => (
              <tr key={t.tester_id} className="border-t" style={{ borderColor: 'var(--color-border)' }} data-testid={`tester-row-${t.username}`}>
                <td className="px-3 py-2 font-medium">{t.username}</td>
                <td className="px-3 py-2">{t.is_active ? 'Active' : 'Disabled'}</td>
                <td className="px-3 py-2">{t.created_at ? new Date(t.created_at).toLocaleDateString('en-GB') : '—'}</td>
                <td className="px-3 py-2">{t.last_login_at ? new Date(t.last_login_at).toLocaleString('en-GB') : '—'}</td>
                <td className="px-3 py-2">
                  {resetFor === t.tester_id ? (
                    <span className="flex gap-2">
                      <input className="input-styled" placeholder="New password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid={`tester-reset-input-${t.username}`} />
                      <button className="btn-primary px-3 py-1" onClick={() => savePassword(t)} data-testid={`tester-reset-save-${t.username}`}>Save</button>
                      <button className="btn-secondary px-3 py-1" onClick={() => setResetFor(null)}>Cancel</button>
                    </span>
                  ) : (
                    <span className="flex gap-2">
                      <button className="btn-secondary px-3 py-1" onClick={() => { setResetFor(t.tester_id); setResetPw(''); }} data-testid={`tester-reset-btn-${t.username}`}>Reset password</button>
                      <button className="btn-secondary px-3 py-1" onClick={() => toggle(t)} data-testid={`tester-toggle-btn-${t.username}`}>{t.is_active ? 'Disable' : 'Enable'}</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!testers.length && <tr><td colSpan={5} className="px-3 py-8 text-center" style={{ color: 'var(--color-muted)' }}>No tester accounts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
